import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectComponent } from '@components/forms/select/select.component';
import { ButtonDirective } from '@directives/button.directive';
import { FocusIntoViewDirective } from '@directives/focus-into-view.directive';
import { Session } from '@models/auth.model';
import { SelectModel } from '@models/common.model';
import { IncreaseCreditRequest, SearchPartnerResponse } from '@models/partner.model';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { NumberFormatPipe } from '@pipes/number-format.pipe';
import { DestroyService } from '@services/destroy.service';
import { DropdownService } from '@services/dropdown.service';
import { PartnerService } from '@services/partner.service';
import { ToastService } from '@services/toast.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { InputNumberModule } from 'primeng/inputnumber';
import { finalize, takeUntil } from 'rxjs';


@Component({
    selector: 'pn-request-credit-popup',
    standalone: true,
    imports: [
        CommonModule,
        SvgIconComponent,
        ReactiveFormsModule,
        ButtonDirective,
        SelectComponent,
        NumberFormatPipe,
        FocusIntoViewDirective,
        InputNumberModule
    ],
    templateUrl: './request-credit-popup.component.html',
    styleUrls: ['./request-credit-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DestroyService],
})
export class RequestCreditPopupComponent implements OnInit {
    private dialogRef = inject(DialogRef);
    private store = inject(Store<StoreModel>);
    private toastService = inject(ToastService);
    private destroyService = inject(DestroyService);
    private partnerService = inject(PartnerService);
    private fb = inject(FormBuilder);
    private cd = inject(ChangeDetectorRef);

    public formGroup = this.createForm();
    public loading = signal(false);
    public partnerLoading = signal(false);
    public partnerOptions: SelectModel[] = [];

    constructor(
        @Inject(DIALOG_DATA)
        public data: {
            isCreate: boolean,
            title: string;
            partner: {
                PartnerId: number;
                PartnerOptions: SelectModel[];
            };
        }
    ) {}

    ngOnInit(): void {
        this.partnerLoading.set(true);
        if (this.data?.partner) {
            this.partnerOptions = this.data.partner.PartnerOptions;
            this.formGroup.patchValue({
                PartnerId: this.data.partner.PartnerId,
                TopupAmount: 0,
                Content: '',
            });
        }
        this.partner.disable({ onlySelf: true, emitEvent: false });
    }

    public get partner(): FormControl {
        return this.formGroup.get('PartnerId') as FormControl;
    }

    /**
     * Create form
     *
     * @private
     * @return {*}
     * @memberof RequestCreditPopupComponent
     */
    private createForm() {
        return this.fb.group({
            PartnerId: this.fb.control<number | null>(null, Validators.required),
            TopupAmount: this.fb.control<number | null>(0, Validators.required),
            Content: this.fb.control('', Validators.required),
        });
    }

    /**
     * submit
     *
     * @return {*}
     * @memberof RequestCreditPopupComponent
     */
    public handleSubmit() {
        if (this.formGroup.invalid || this.loading()) {
            return;
        }
        const { TopupAmount, Content } = this.formGroup.value;
        const reqBody: IncreaseCreditRequest = {
            PartnerId: this.partner.value,
            TotalBalance: TopupAmount || 0,
            Content: Content as string,
        };
        this.loading.set(true);
        if (this.data.isCreate) {
            this.handleCreateBalance(reqBody);
        } else {
            this.handleRevertBalance(reqBody);
        }
    }

    private handleCreateBalance(reqBody: IncreaseCreditRequest) {
        this.partnerService
            .createIncreaseCreditRequest(reqBody)
            .pipe(
                finalize(() => this.loading.set(false)),
                takeUntil(this.destroyService)
            )
            .subscribe((res) => {
                if (res?.ID > 0) {
                    this.toastService.showSuccess(
                        'Yêu cầu tín dụng',
                        res.Message
                    );
                    this.dialogRef.close();
                    return;
                }
                this.toastService.showError(
                    'Yêu cầu tín dụng',
                    res?.Message || 'Yêu cầu tín dụng không thành công!'
                );
            });
    }

    private handleRevertBalance(reqBody: IncreaseCreditRequest) {
        this.partnerService
            .revertIncreaseCreditRequest(reqBody)
            .pipe(
                finalize(() => this.loading.set(false)),
                takeUntil(this.destroyService)
            )
            .subscribe((res) => {
                if (res?.ID > 0) {
                    this.toastService.showSuccess(
                        'Thu hồi số dư',
                        res.Message
                    );
                    this.dialogRef.close();
                    return;
                }
                this.toastService.showError(
                    'Thu hồi số dư',
                    res?.Message || 'Thu hồi số dư không thành công!'
                );
            });
    }

    public handleClose() {
        this.dialogRef.close();
    }
}
